<script setup lang="ts">
/**
 * Wraps projected content in a minimal browser window.
 *
 * Given an `href`, the whole frame becomes a link that opens in a new tab and
 * the address bar shows that link's host — so the chrome carries real
 * information (where this goes) rather than decoration. On hover or keyboard
 * focus the content dims and a call to action fades in.
 *
 * ```vue
 * <BrowserFrame :href="live">
 *   <ResponsiveImage … />
 * </BrowserFrame>
 * ```
 */
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    /** Destination. Omit to render an inert frame with no link behavior. */
    href?: string;
    /** Address bar text. Defaults to the host parsed from `href`. */
    label?: string;
    /** Overlay call to action. */
    cta?: string;
  }>(),
  {
    href: undefined,
    label: undefined,
    cta: "Click to view project",
  },
);

const isLink = computed(() => Boolean(props.href));

const address = computed(() => {
  if (props.label) return props.label;
  if (!props.href) return "";
  try {
    return new URL(props.href).host;
  } catch {
    // Relative or malformed — show it as given rather than nothing.
    return props.href;
  }
});
</script>

<template>
  <component
    :is="isLink ? 'a' : 'div'"
    class="browser-frame"
    :href="href"
    :target="isLink ? '_blank' : undefined"
    :rel="isLink ? 'noopener' : undefined"
  >
    <div class="chrome">
      <span class="dots" aria-hidden="true"><i /><i /><i /></span>
      <span v-if="address" class="address">{{ address }}</span>
    </div>

    <div class="viewport">
      <slot />

      <!-- The link is already named by the slotted image's alt text, so the
           visible CTA would only duplicate it for a screen reader. -->
      <span v-if="isLink" class="cta" aria-hidden="true">
        <span class="cta-label">
          <span class="slashes">////</span>
          {{ cta }}
        </span>
      </span>
    </div>

    <span v-if="isLink" class="new-tab-hint">(opens in a new tab)</span>
  </component>
</template>

<style scoped lang="scss">
.browser-frame {
  display: block;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.25s ease;
}

a.browser-frame:hover,
a.browser-frame:focus-visible {
  border-color: var(--color-accent);
}

a.browser-frame:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 3px;
}

/* Window bar. Hairline outlined dots rather than filled macOS lights — the
   window cue without the pastiche. */
.chrome {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: 0.6rem var(--space-md);
  background-color: var(--color-surface-solid);
  border-bottom: 1px solid var(--color-border);
}

.dots {
  display: inline-flex;
  gap: 0.4rem;
  flex-shrink: 0;
}

.dots i {
  width: 8px;
  height: 8px;
  border: 1px solid var(--color-text-subtle);
  border-radius: 50%;
}

.address {
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-subtle);
  letter-spacing: 0.04em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewport {
  position: relative;
  display: block;
  /* No `font-size: 0` here to close the inline-descender gap — the type scale
     is em-based, so it would collapse the CTA label to zero. `display: block`
     on the image below closes that gap instead. */
}

.viewport :deep(img) {
  display: block;
  width: 100%;
  height: auto;
}

/* Dim toward the page background so it reads correctly in both themes. */
.cta {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: color-mix(
    in oklch,
    var(--color-background) 78%,
    transparent
  );
  opacity: 0;
  transition: opacity 0.25s ease;
}

.cta-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--color-text-base);
  transform: translateY(4px);
  transition: transform 0.25s cubic-bezier(0.85, 0.09, 0.15, 0.91);
}

.slashes {
  font-weight: 400;
  letter-spacing: 0.05em;
}

/* Only bind the reveal to hover where hovering exists. On touch the address
   bar is the affordance, and the frame is still a link. */
@media (hover: hover) {
  .browser-frame:hover .cta {
    opacity: 1;
  }

  .browser-frame:hover .cta-label {
    transform: translateY(0);
  }
}

.browser-frame:focus-visible .cta {
  opacity: 1;
}

.browser-frame:focus-visible .cta-label {
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .browser-frame,
  .cta,
  .cta-label {
    transition: none;
  }

  .cta-label {
    transform: none;
  }
}

/* Announced, never shown: the visible CTA is aria-hidden, so this is what
   tells a screen reader the link leaves the page. */
.new-tab-hint {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
